import React, { forwardRef } from 'react';

const Input = forwardRef(({
    label,
    error,
    icon: Icon,
    onIconClick,
    className = '',
    ...props
}, ref) => {
    
    // Automatic limits and sanitization across the entire platform
    const isPhone = props.type === 'tel' || /phone|mobile|contact/i.test(props.name || '');
    const isPincode = /pincode|zip/i.test(props.name || '');
    const isName = /name/i.test(props.name || '');

    // Set intelligent max lengths based on the field type
    const defaultMaxLength = props.maxLength || (isPhone ? 10 : isPincode ? 6 : isName ? 100 : 255);

    const handleChange = (e) => {
        if (!props.onChange) return;
        
        let val = e.target.value || '';
        
        // Strip non-numeric characters for phone numbers and pincodes instantly
        if (isPhone) {
            val = val.toString().replace(/\D/g, '').slice(0, 10);
        } else if (isPincode) {
            val = val.toString().replace(/\D/g, '').slice(0, 6);
        }
        
        // If the value changed due to our stripping, create a synthetic-like event
        if (val !== e.target.value) {
            const syntheticEvent = {
                ...e,
                target: { ...e.target, value: val, name: e.target.name },
                currentTarget: { ...e.currentTarget, value: val, name: e.target.name }
            };
            props.onChange(syntheticEvent);
        } else {
            props.onChange(e);
        }
    };

    return (
        <div className={className}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                    {props.required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                {Icon && (
                    <div
                        className={`absolute inset-y-0 ${onIconClick ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center ${onIconClick ? 'cursor-pointer hover:text-gray-600' : 'pointer-events-none'}`}
                        onClick={onIconClick}
                    >
                        <Icon className="h-5 w-5 text-gray-400" />
                    </div>
                )}
                <input
                    ref={ref}
                    className={`block w-full rounded-lg border ${error ? 'border-red-300' : 'border-gray-300 dark:border-[#1a3884]/45'
                        } ${Icon && !onIconClick ? 'pl-10' : 'pl-3'} ${Icon && onIconClick ? 'pr-10' : 'pr-3'} py-2 bg-white dark:bg-[#002147] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff]/50 focus:border-transparent`}
                    maxLength={defaultMaxLength}
                    {...props}
                    onChange={handleChange}
                />
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
});

Input.displayName = 'Input';

export default Input;

